import type { CallsClient } from '@wtfalch/calls';
import { useCallback, useEffect, useRef, useState } from 'react';

import { describeCallError } from './useCallEndpoint.ts';

/** The public page a guest opens. The secret rides in the fragment, which a
 *  browser never sends to a server and a proxy never logs. */
export const GUEST_CALL_PATH = '/call/guest';

/** How long a link made from inside a call stays good, and how many guests it
 *  admits to the lobby. Members still decide each one. */
const LINK_WINDOW_MS = 12 * 60 * 60 * 1000;
const LINK_REDEMPTIONS = 25;

/** The address to hand a guest. Only ever built for the clipboard and the
 *  panel: it is never written to a query string, a log or the history. */
export function guestLinkUrl(origin: string, secret: string): string {
  return `${origin}${GUEST_CALL_PATH}#${encodeURIComponent(secret)}`;
}

export type GuestLink = { linkId: string; url: string };

export type GuestLinkControl = {
  link: GuestLink | null;
  busy: boolean;
  error: string | null;
  /** Makes a link, or returns the one already made in this call. */
  create: () => Promise<GuestLink | null>;
  revoke: () => Promise<void>;
};

/** Links made in this window, by client and session. The call screen
 *  unmounts whenever a member leaves the full call view, and the service never
 *  returns a secret again, so the link lives here rather than in its state. */
const outstanding = new WeakMap<CallsClient, Map<string, GuestLink>>();

function linksOf(client: CallsClient): Map<string, GuestLink> {
  let links = outstanding.get(client);
  if (!links) {
    links = new Map();
    outstanding.set(client, links);
  }
  return links;
}

function storedLink(client: CallsClient | undefined, sessionId: string): GuestLink | null {
  return (client && outstanding.get(client)?.get(sessionId)) ?? null;
}

/**
 * A guest link for one call, made on demand by a member in it and revoked on
 * request. The link outlives the call screen, so a guest it was sent to can
 * still join after the member leaves the full call view.
 */
export function useGuestLink(client: CallsClient | undefined, sessionId: string): GuestLinkControl {
  const [link, setLink] = useState<GuestLink | null>(() => storedLink(client, sessionId));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const linkRef = useRef<GuestLink | null>(storedLink(client, sessionId));
  const sessionRef = useRef(sessionId);
  const busyRef = useRef(false);

  const create = useCallback(async () => {
    if (!client) return null;
    if (linkRef.current) return linkRef.current;
    if (busyRef.current) return null;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const now = Date.now();
      const made = await client.createGuestLink(sessionId, {
        validFrom: now,
        expiresAt: now + LINK_WINDOW_MS,
        maxRedemptions: LINK_REDEMPTIONS,
        requestId: crypto.randomUUID(),
      });
      if (!made.secret) {
        setError('The link could not be made. Try again.');
        return null;
      }
      const next = { linkId: made.linkId, url: guestLinkUrl(window.location.origin, made.secret) };
      linksOf(client).set(sessionId, next);
      linkRef.current = next;
      setLink(next);
      return next;
    } catch (failure) {
      setError(describeCallError(failure, 'Could not make the link.'));
      return null;
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [client, sessionId]);

  const revoke = useCallback(async () => {
    const current = linkRef.current;
    if (!client || !current || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await client.revokeGuestLink(sessionId, {
        linkId: current.linkId,
        requestId: crypto.randomUUID(),
      });
      linksOf(client).delete(sessionId);
      linkRef.current = null;
      setLink(null);
    } catch (failure) {
      setError(describeCallError(failure, 'Could not revoke the link.'));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [client, sessionId]);

  // A new `sessionId` means the call this link was for was replaced: revoke
  // the old link, which nothing can show again. Unmounting keeps it.
  useEffect(() => {
    const previous = sessionRef.current;
    if (previous === sessionId) return;
    sessionRef.current = sessionId;
    const old = storedLink(client, previous);
    if (client) linksOf(client).delete(previous);
    const current = storedLink(client, sessionId);
    linkRef.current = current;
    setLink(current);
    if (old && client) {
      client
        .revokeGuestLink(previous, { linkId: old.linkId, requestId: crypto.randomUUID() })
        .catch((failure: unknown) =>
          console.error('The old guest link could not be revoked', failure),
        );
    }
  }, [client, sessionId]);

  return { link, busy, error, create, revoke };
}
